import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polyline,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  MapPin,
  Route,
  ArrowRight,
  Star,
  X,
  Footprints,
  Play,
  Square,
} from "lucide-react";
import { loadConfiguredGoogleMaps, MapControls } from "./GoogleMapView";

// Иконки
const spotIcon = new L.Icon({
  iconUrl: "https://cdn-icons-png.flaticon.com/512/684/684908.png",
  iconSize: [34, 34],
  iconAnchor: [17, 34],
  popupAnchor: [0, -34],
});

const activeSpotIcon = new L.Icon({
  iconUrl: "https://cdn-icons-png.flaticon.com/512/447/447031.png",
  iconSize: [40, 40],
  iconAnchor: [20, 40],
  popupAnchor: [0, -40],
});

const userIcon = new L.Icon({
  iconUrl: "https://cdn-icons-png.flaticon.com/512/149/149060.png",
  iconSize: [34, 34],
  iconAnchor: [17, 34],
  popupAnchor: [0, -34],
});

// Автоматическое слежение камеры за пользователем при ведении
function FollowUserMap({ coords, isNavigating }) {
  const map = useMap();
  useEffect(() => {
    if (coords && isNavigating) {
      map.flyTo([coords.lat, coords.lng], 17, { duration: 0.8 });
    }
  }, [map, coords, isNavigating]);
  return null;
}

function FocusDestinationMap({ destination }) {
  const map = useMap();
  useEffect(() => {
    if (destination?.coordinates) {
      map.flyTo([destination.coordinates.lat, destination.coordinates.lng], 16, { duration: 0.8 });
    }
  }, [destination, map]);
  return null;
}

function InteractiveMapControls({ onLocate }) {
  const map = useMap();
  return (
    <MapControls
      onZoomIn={() => map.zoomIn()}
      onZoomOut={() => map.zoomOut()}
      onLocate={onLocate}
    />
  );
}

function InteractiveGoogleMap({
  mapsApi,
  center,
  spots,
  activeSpot,
  userLocation,
  isNavigating,
  routeCoordinates,
  onLocate,
  onSelectSpot,
  onBuildRoute,
  loadingRoute,
  routeDestination,
  t,
}) {
  const mapElementRef = useRef(null);
  const mapRef = useRef(null);
  const spotMarkersRef = useRef([]);
  const userMarkerRef = useRef(null);
  const routeLineRef = useRef(null);
  const infoWindowRef = useRef(null);
  const callbacksRef = useRef(null);
  const [popup, setPopup] = useState(null);
  callbacksRef.current = { onSelectSpot, onBuildRoute, loadingRoute, routeDestination };
  const centerLat = Number(center[0]);
  const centerLng = Number(center[1]);

  const spotsSignature = spots
    .map((spot) => `${spot.id}:${spot.coordinates?.lat}:${spot.coordinates?.lng}`)
    .join("|");

  useEffect(() => {
    if (!mapsApi || !mapElementRef.current) return undefined;
    const map = new mapsApi.Map(mapElementRef.current, {
      center: { lat: centerLat, lng: centerLng },
      zoom: 13,
      colorScheme: "LIGHT",
      disableDefaultUI: true,
      clickableIcons: false,
      fullscreenControl: false,
      mapTypeControl: false,
      streetViewControl: false,
      keyboardShortcuts: false,
      zoomControl: false,
      gestureHandling: "cooperative",
    });
    const infoWindow = new mapsApi.InfoWindow();
    const closeListener = infoWindow.addListener("closeclick", () => setPopup(null));
    mapRef.current = map;
    infoWindowRef.current = infoWindow;

    return () => {
      mapsApi.event.removeListener(closeListener);
      infoWindow.close();
      spotMarkersRef.current.forEach(({ marker, listener }) => {
        if (listener) mapsApi.event.removeListener(listener);
        marker.setMap(null);
      });
      spotMarkersRef.current = [];
      userMarkerRef.current?.setMap(null);
      userMarkerRef.current = null;
      routeLineRef.current?.setMap(null);
      routeLineRef.current = null;
      mapRef.current = null;
      infoWindowRef.current = null;
    };
  }, [mapsApi, centerLat, centerLng]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const position = isNavigating && userLocation
      ? userLocation
      : activeSpot?.coordinates || null;
    if (!position) return;
    map.panTo({ lat: Number(position.lat), lng: Number(position.lng) });
    map.setZoom(isNavigating && userLocation ? 17 : 16);
  }, [activeSpot, isNavigating, userLocation]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return undefined;
    spotMarkersRef.current.forEach(({ marker, listener }) => {
      if (listener) mapsApi.event.removeListener(listener);
      marker.setMap(null);
    });
    spotMarkersRef.current = spots.filter((spot) => spot.coordinates).map((spot) => {
      const marker = new mapsApi.Marker({
        map,
        position: { lat: Number(spot.coordinates.lat), lng: Number(spot.coordinates.lng) },
        title: spot.title,
        icon: {
          path: mapsApi.SymbolPath.CIRCLE,
          scale: activeSpot?.id === spot.id ? 10 : 8,
          fillColor: activeSpot?.id === spot.id ? "#f59e0b" : "#fbbf24",
          fillOpacity: 1,
          strokeColor: "#ffffff",
          strokeWeight: 3,
        },
      });
      const listener = marker.addListener("click", () => {
        const callbacks = callbacksRef.current;
        callbacks.onSelectSpot(spot);
        const content = document.createElement("div");
        infoWindowRef.current?.setContent(content);
        infoWindowRef.current?.open({ anchor: marker, map });
        setPopup({ spot, content });
      });
      return { marker, listener, spot };
    });

    return () => {
      spotMarkersRef.current.forEach(({ marker, listener }) => {
        if (listener) mapsApi.event.removeListener(listener);
        marker.setMap(null);
      });
      spotMarkersRef.current = [];
    };
    // Callbacks are kept current in a ref so markers need rebuilding only when
    // the spot data itself changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapsApi, spotsSignature]);

  useEffect(() => {
    spotMarkersRef.current.forEach(({ marker, spot }) => {
      const selected = activeSpot?.id === spot.id;
      marker.setIcon({
        path: mapsApi.SymbolPath.CIRCLE,
        scale: selected ? 10 : 8,
        fillColor: selected ? "#f59e0b" : "#fbbf24",
        fillOpacity: 1,
        strokeColor: "#ffffff",
        strokeWeight: 3,
      });
    });
  }, [activeSpot?.id, mapsApi]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (userLocation) {
      const position = { lat: Number(userLocation.lat), lng: Number(userLocation.lng) };
      if (!userMarkerRef.current) {
        userMarkerRef.current = new mapsApi.Marker({
          map,
          position,
          title: t("map.you_are_here"),
          icon: {
            path: mapsApi.SymbolPath.CIRCLE,
            scale: 8,
            fillColor: "#3b82f6",
            fillOpacity: 1,
            strokeColor: "#ffffff",
            strokeWeight: 3,
          },
        });
      } else {
        userMarkerRef.current.setPosition(position);
      }
    } else if (userMarkerRef.current) {
      userMarkerRef.current.setMap(null);
      userMarkerRef.current = null;
    }
  }, [mapsApi, userLocation, t]);

  useEffect(() => {
    if (!mapRef.current) return;
    routeLineRef.current?.setMap(null);
    routeLineRef.current = routeCoordinates.length > 1
      ? new mapsApi.Polyline({
        map: mapRef.current,
        path: routeCoordinates.map(([lat, lng]) => ({ lat: Number(lat), lng: Number(lng) })),
        geodesic: true,
        strokeColor: "#F59E0B",
        strokeOpacity: 0.85,
        strokeWeight: 6,
      })
      : null;
  }, [mapsApi, routeCoordinates]);

  const zoomMap = (direction) => {
    const map = mapRef.current;
    if (map) map.setZoom((map.getZoom() || 13) + direction);
  };

  return (
    <div className="absolute inset-0">
      <div ref={mapElementRef} className="absolute inset-0" role="application" aria-label="Map" />
      <MapControls onZoomIn={() => zoomMap(1)} onZoomOut={() => zoomMap(-1)} onLocate={onLocate} />
      {popup && createPortal(
        <div className="p-1 min-w-[180px] max-w-[240px]">
          <span className="text-[10px] font-bold text-amber-600 uppercase tracking-wider block mb-0.5">
            {t(`cities.${popup.spot.citySlug}`, popup.spot.cityName)}
          </span>
          <h4 className="font-bold text-slate-900 text-sm mb-1">
            {t(`spots_detail_data.${popup.spot.id}.title`, popup.spot.title)}
          </h4>
          {popup.spot.isExternalCatalog ? (
            <p className="mb-2 text-xs text-slate-500">{popup.spot.description}</p>
          ) : (
            <Link
              to={`/spots/${popup.spot.id}`}
              className="inline-flex items-center gap-1 text-xs text-amber-600 font-bold hover:underline mb-2"
            >
              {t("map.details")} <ArrowRight className="w-3 h-3" />
            </Link>
          )}
          <button
            type="button"
            onClick={() => callbacksRef.current?.onBuildRoute(popup.spot)}
            className="w-full bg-amber-400 hover:bg-amber-500 text-slate-950 font-bold py-1.5 px-2 rounded-lg text-xs flex items-center justify-center gap-1 transition-colors"
          >
            <Route className="w-3.5 h-3.5" />
            {callbacksRef.current?.loadingRoute && callbacksRef.current?.routeDestination?.id === popup.spot.id
              ? t("map.building")
              : t("map.route")}
          </button>
        </div>,
        popup.content
      )}
    </div>
  );
}

export default function InteractiveMap({ spots = [], initialDestination = null }) {
  const { t } = useTranslation();
  const bukharaCenter = [39.775, 64.418];

  const [activeSpot, setActiveSpot] = useState(initialDestination);
  const [routeDestination, setRouteDestination] = useState(null);
  const [routeCoordinates, setRouteCoordinates] = useState([]);
  const [routeSteps, setRouteSteps] = useState([]); // Пошаговые инструкции
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [routeInfo, setRouteInfo] = useState(null);

  const [userLocation, setUserLocation] = useState(null);
  const [isNavigating, setIsNavigating] = useState(false); // Активен ли навигатор
  const [, setLoadingGeo] = useState(false);
  const [loadingRoute, setLoadingRoute] = useState(false);
  const [googleMapsApi, setGoogleMapsApi] = useState(null);

  const watchIdRef = useRef(null);

  useEffect(() => {
    let active = true;
    loadConfiguredGoogleMaps()
      .then((api) => { if (active) setGoogleMapsApi(api); })
      .catch((error) => console.warn("Google Maps could not be loaded; using OpenStreetMap instead.", error));
    return () => { active = false; };
  }, []);

  // Определение единичной геопозиции
  const getUserGeo = (callback) => {
    if (userLocation) {
      if (callback) callback(userLocation);
      return;
    }

    if (!navigator.geolocation) return;
    setLoadingGeo(true);

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setUserLocation(coords);
        setLoadingGeo(false);
        if (callback) callback(coords);
      },
      () => setLoadingGeo(false),
      { enableHighAccuracy: true }
    );
  };

  const handleLocateUser = () => {
    getUserGeo((coords) => {
      setActiveSpot({ coordinates: coords });
    });
  };

  // Построение маршрута прямо на сайте через OSRM
  const handleBuildRoute = (spot) => {
    setActiveSpot(spot);
    getUserGeo(async (userCoords) => {
      setRouteDestination(spot);
      setLoadingRoute(true);

      try {
        const response = await fetch(
          `https://router.project-osrm.org/route/v1/foot/${userCoords.lng},${userCoords.lat};${spot.coordinates.lng},${spot.coordinates.lat}?overview=full&steps=true&geometries=geojson`
        );
        const data = await response.json();

        if (data.routes && data.routes[0]) {
          const route = data.routes[0];
          const coords = route.geometry.coordinates.map((c) => [c[1], c[0]]);
          setRouteCoordinates(coords);

          // Извлекаем пошаговые подсказки
          const steps = route.legs[0].steps.map((s) => ({
            instruction: s.maneuver.modifier
              ? t("map.turn", { direction: s.maneuver.modifier, street: s.name || t("map.street") })
              : t("map.straight", { street: s.name || t("map.road") }),
            distance: Math.round(s.distance),
          }));
          setRouteSteps(steps);
          setCurrentStepIndex(0);

          const distanceKm = (route.distance / 1000).toFixed(1);
          const durationMin = Math.round(route.duration / 60);

          setRouteInfo({
            distance: `${distanceKm} ${t("map.km")}`,
            duration: `${durationMin} ${t("map.min")}`,
          });
        }
      } catch (e) {
        setRouteCoordinates([
          [userCoords.lat, userCoords.lng],
          [spot.coordinates.lat, spot.coordinates.lng],
        ]);
      } finally {
        setLoadingRoute(false);
      }
    });
  };

  // Старт навигатора (GPS в реальном времени)
  const handleStartLiveNavigation = () => {
    if (!navigator.geolocation) return;
    setIsNavigating(true);

    // Подключаем непрерывное отслеживание позиции человека
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const newCoords = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        };
        setUserLocation(newCoords);
      },
      (err) => console.error(err),
      { enableHighAccuracy: true, maximumAge: 1000, timeout: 5000 }
    );
  };

  // Остановка навигатора
  const handleStopLiveNavigation = () => {
    setIsNavigating(false);
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
  };

  // Сброс маршрута
  const handleClearRoute = () => {
    handleStopLiveNavigation();
    setRouteDestination(null);
    setRouteCoordinates([]);
    setRouteSteps([]);
    setRouteInfo(null);
  };

  useEffect(() => {
    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (initialDestination) setActiveSpot(initialDestination);
  }, [initialDestination]);

  return (
    <div className="space-y-6">
      <div className="relative w-full h-[68svh] min-h-[420px] max-h-[550px] rounded-2xl sm:rounded-3xl overflow-hidden border border-slate-200/60 dark:border-slate-800 shadow-md">

        {/* Кнопка сброса и геопозиции */}
        {routeDestination && (
          <div className="absolute left-3 top-3 z-[1000] sm:left-4 sm:top-4">
            <button
              onClick={handleClearRoute}
              className="flex items-center gap-1.5 rounded-2xl bg-red-500 px-3.5 py-2 text-xs font-bold text-white shadow-lg transition-all hover:bg-red-600"
            >
              <X className="h-4 w-4" />
              <span>{t("map.reset")}</span>
            </button>
          </div>
        )}

        {/* Панель живой навигации (подсказки над картой) */}
        {isNavigating && routeSteps.length > 0 && (
          <div className="absolute top-4 left-4 z-[1000] bg-slate-950/90 text-white p-4 rounded-2xl shadow-2xl border border-amber-500/50 max-w-[280px] sm:max-w-xs animate-pulse">
            <span className="text-[10px] font-bold text-amber-400 uppercase tracking-widest block mb-1">
              📍 {t("map.navigator_active")}
            </span>
            <p className="text-sm font-bold">
              {routeSteps[currentStepIndex]?.instruction || t("map.follow_line")}
            </p>
            {routeSteps[currentStepIndex]?.distance > 0 && (
              <span className="text-xs text-slate-400 mt-1 block">
                {t("map.in_meters", { count: routeSteps[currentStepIndex]?.distance })}
              </span>
            )}
          </div>
        )}

        {/* Плашка маршрута с кнопкой "Начать движение" */}
        {routeDestination && !isNavigating && (
          <div className="absolute bottom-4 left-4 z-[1000] bg-white/95 dark:bg-slate-900/95 backdrop-blur-md p-4 rounded-2xl shadow-xl border border-slate-200/60 dark:border-slate-800 max-w-[280px] sm:max-w-sm">
            <div className="flex items-center gap-2 text-amber-500 text-xs font-bold uppercase tracking-wider mb-1">
              <Footprints className="w-4 h-4" />
              <span>{t("map.route_ready")}</span>
            </div>
            <h4 className="font-serif font-bold text-slate-900 dark:text-white text-sm sm:text-base line-clamp-1 mb-1">
              {t("map.to_place", { place: t(`spots_detail_data.${routeDestination.id}.title`, routeDestination.title) })}
            </h4>

            {routeInfo && (
              <div className="flex items-center gap-3 text-xs text-slate-600 dark:text-slate-300 font-semibold mb-3">
                <span>📍 {routeInfo.distance}</span>
                <span>•</span>
                <span>⏱️ {routeInfo.duration} {t("map.walking")}</span>
              </div>
            )}

            <button
              onClick={handleStartLiveNavigation}
              className="w-full bg-amber-400 hover:bg-amber-500 text-slate-950 font-bold py-2.5 px-3 rounded-xl text-xs flex items-center justify-center gap-2 transition-colors shadow-sm"
            >
              <Play className="w-4 h-4 fill-slate-950" /> {t("map.start_navigation")}
            </button>
          </div>
        )}

        {/* Кнопка остановки ведения */}
        {isNavigating && (
          <div className="absolute bottom-4 left-4 z-[1000]">
            <button
              onClick={handleStopLiveNavigation}
              className="bg-red-500 hover:bg-red-600 text-white font-bold py-3 px-5 rounded-2xl text-xs flex items-center gap-2 shadow-2xl transition-all"
            >
              <Square className="w-4 h-4 fill-white" /> {t("map.stop_navigation")}
            </button>
          </div>
        )}

        {googleMapsApi ? (
          <InteractiveGoogleMap
            mapsApi={googleMapsApi}
            center={bukharaCenter}
            spots={spots}
            activeSpot={activeSpot}
            userLocation={userLocation}
            isNavigating={isNavigating}
            routeCoordinates={routeCoordinates}
            onLocate={handleLocateUser}
            onSelectSpot={setActiveSpot}
            onBuildRoute={handleBuildRoute}
            loadingRoute={loadingRoute}
            routeDestination={routeDestination}
            t={t}
          />
        ) : (
          <MapContainer
            center={bukharaCenter}
            zoom={13}
            scrollWheelZoom={false}
            zoomControl={false}
            className="w-full h-full z-0"
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            <FollowUserMap coords={userLocation} isNavigating={isNavigating} />
            <FocusDestinationMap destination={activeSpot} />
            <InteractiveMapControls onLocate={handleLocateUser} />

            {routeCoordinates.length > 0 && (
              <Polyline
                positions={routeCoordinates}
                pathOptions={{ color: "#F59E0B", weight: 6, opacity: 0.85 }}
              />
            )}

            {userLocation && (
              <Marker position={[userLocation.lat, userLocation.lng]} icon={userIcon}>
                <Popup>{t("map.you_are_here")}</Popup>
              </Marker>
            )}

            {spots.map((spot) => {
              const isSelected = activeSpot?.id === spot.id;
              return (
                <Marker
                  key={spot.id}
                  position={[spot.coordinates.lat, spot.coordinates.lng]}
                  icon={isSelected ? activeSpotIcon : spotIcon}
                  eventHandlers={{ click: () => setActiveSpot(spot) }}
                >
                  <Popup>
                    <div className="p-1 min-w-[160px]">
                      <span className="text-[10px] font-bold text-amber-600 uppercase tracking-wider block mb-0.5">
                        {t(`cities.${spot.citySlug}`, spot.cityName)}
                      </span>
                      <h4 className="font-bold text-slate-900 text-sm mb-1">{t(`spots_detail_data.${spot.id}.title`, spot.title)}</h4>
                      {spot.isExternalCatalog ? (
                        <p className="mb-2 text-xs text-slate-500">{spot.description}</p>
                      ) : (
                        <Link
                          to={`/spots/${spot.id}`}
                          className="inline-flex items-center gap-1 text-xs text-amber-600 font-bold hover:underline mb-2 block"
                        >
                          {t("map.details")} <ArrowRight className="w-3 h-3" />
                        </Link>
                      )}

                      <button
                        onClick={() => handleBuildRoute(spot)}
                        className="w-full bg-amber-400 hover:bg-amber-500 text-slate-950 font-bold py-1.5 px-2 rounded-lg text-xs flex items-center justify-center gap-1 transition-colors"
                      >
                        <Route className="w-3.5 h-3.5" />
                        {loadingRoute && routeDestination?.id === spot.id ? t("map.building") : t("map.route")}
                      </button>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
        )}
      </div>

      {/* Карточки под картой */}
      <div>
        <h3 className="font-serif text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-100 mb-4">
          {t("map.popular_places")}
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {spots.map((spot) => {
            const isSelected = activeSpot?.id === spot.id;
            const isRouteActive = routeDestination?.id === spot.id;

            return (
              <div
                key={spot.id}
                className={`bg-white dark:bg-slate-900 rounded-2xl p-3.5 border transition-all duration-300 flex flex-col justify-between ${
                  isRouteActive
                    ? "border-amber-500 ring-2 ring-amber-400/40 shadow-lg"
                    : isSelected
                    ? "border-amber-400 dark:border-amber-500 ring-1 ring-amber-400/20"
                    : "border-slate-200/70 dark:border-slate-800"
                }`}
              >
                <div>
                  {spot.heroImage ? <div className="relative h-28 w-full rounded-xl overflow-hidden mb-3">
                    <img
                      src={spot.heroImage}
                      alt={spot.title}
                      className="w-full h-full object-cover"
                    />
                    {spot.rating && <div className="absolute top-2 right-2 bg-slate-950/70 backdrop-blur-md px-2 py-0.5 rounded-lg text-white text-[10px] font-bold flex items-center gap-1">
                      <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                      {spot.rating}
                    </div>}
                  </div> : <div className="mb-3 flex h-28 items-center justify-center rounded-xl bg-gradient-to-br from-amber-100 to-orange-50 dark:from-slate-800 dark:to-slate-900"><MapPin className="h-8 w-8 text-amber-500" /></div>}

                  <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wider block mb-1">
                    {t(`cities.${spot.citySlug}`, spot.cityName)}
                  </span>
                  <h4 className="font-serif font-bold text-slate-900 dark:text-slate-100 text-sm mb-1 line-clamp-1">
                    {t(`spots_detail_data.${spot.id}.title`, spot.title)}
                  </h4>
                  <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2 mb-3">
                    {t(`spots_detail_data.${spot.id}.description`, spot.description)}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                  <button
                    onClick={() => setActiveSpot(spot)}
                    className="py-2 px-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 transition-all"
                  >
                    <MapPin className="w-3.5 h-3.5 text-amber-500" />
                    <span className="truncate">{t("map.on_map")}</span>
                  </button>

                  <button
                    onClick={() => handleBuildRoute(spot)}
                    className="py-2 px-2 bg-amber-400 hover:bg-amber-500 text-slate-950 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-all"
                  >
                    <Route className="w-3.5 h-3.5" />
                    <span className="truncate">{t("map.route")}</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
